import { getEffectiveConsent, type ConsentCategories } from './cookieConsent';

type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

type AnalyticsPayload = {
  event: string;
  properties: Record<string, string | number | boolean | null>;
  page: {
    path: string;
    title: string;
    referrer: string;
  };
  sessionId: string;
  timestamp: string;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const ANALYTICS_SESSION_KEY = 'vadauro_analytics_session_id';
const MAX_PROPERTY_LENGTH = 180;

// Session recording is disabled on this app because it handles sensitive account and checkout data.
let gaScriptLoaded = false;
let gtagStubReady = false;
let consentDefaultsSet = false;

export function isSensitiveAnalyticsPath(path: string) {
  return /^\/(reset-password|verify-email|login|create-account|checkout|settings|admin|orders|measurement|my-ai-photos)(?:\/|$)/.test(path);
}

export function safeAnalyticsUrl(value: string) {
  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin ? url.pathname : url.origin;
  } catch {
    return '';
  }
}

function getAnalyticsEnabled() {
  return import.meta.env.VITE_ANALYTICS_ENABLED !== 'false';
}

function getGaMeasurementId() {
  return (import.meta.env.VITE_GA_MEASUREMENT_ID ?? '').trim();
}

function getAnalyticsEndpoint() {
  return (import.meta.env.VITE_ANALYTICS_ENDPOINT ?? '').trim();
}

function getSessionId() {
  if (typeof window === 'undefined') {
    return 'server';
  }

  try {
    const existingSessionId = window.sessionStorage.getItem(ANALYTICS_SESSION_KEY);
    if (existingSessionId) {
      return existingSessionId;
    }

    const nextSessionId = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(ANALYTICS_SESSION_KEY, nextSessionId);
    return nextSessionId;
  } catch {
    return 'unavailable';
  }
}

function normalizePropertyValue(value: AnalyticsProperties[string]) {
  if (value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    return value.slice(0, MAX_PROPERTY_LENGTH);
  }

  if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return value;
  }

  return String(value).slice(0, MAX_PROPERTY_LENGTH);
}

export function sanitizeAnalyticsProperties(properties: AnalyticsProperties = {}) {
  return Object.entries(properties).reduce<Record<string, string | number | boolean | null>>((nextProperties, [key, value]) => {
    const normalizedKey = key.replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 64);
    if (!normalizedKey) {
      return nextProperties;
    }

    nextProperties[normalizedKey] = normalizePropertyValue(value);
    return nextProperties;
  }, {});
}

function buildAnalyticsPayload(event: string, properties?: AnalyticsProperties): AnalyticsPayload {
  const path = typeof window === 'undefined'
    ? '/'
    : window.location.pathname;

  return {
    event,
    properties: sanitizeAnalyticsProperties(properties),
    page: {
      path,
      title: typeof document === 'undefined' ? '' : document.title,
      referrer: typeof document === 'undefined' ? '' : safeAnalyticsUrl(document.referrer),
    },
    sessionId: getSessionId(),
    timestamp: new Date().toISOString(),
  };
}

// ── Consent gating (Google Consent Mode v2) ───────────────────────────────
// GA is treated as "Performance & analytics". They load ONLY after
// consent is granted, and are revoked (recording stopped + cookies cleared)
// when consent is withdrawn. Existing tracking behavior is unchanged once
// analytics consent is granted.

function ensureGtagStub() {
  if (gtagStubReady || typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = window.gtag ?? function gtag(...args: unknown[]) {
    window.dataLayer?.push(args);
  };
  gtagStubReady = true;
}

// Safe defaults — everything denied until the user consents. Must run before
// GA loads so Consent Mode applies from the first hit.
function setConsentDefaults() {
  if (consentDefaultsSet || typeof window === 'undefined') {
    return;
  }
  ensureGtagStub();
  window.gtag?.('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  consentDefaultsSet = true;
}

function loadGoogleAnalyticsOnce() {
  if (gaScriptLoaded || typeof document === 'undefined') {
    return;
  }
  const gaMeasurementId = getGaMeasurementId();
  if (!gaMeasurementId) {
    return;
  }

  ensureGtagStub();
  window.gtag?.('js', new Date());
  window.gtag?.('config', gaMeasurementId, { send_page_view: false, page_location: `${window.location.origin}${window.location.pathname}`, page_referrer: safeAnalyticsUrl(document.referrer) });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaMeasurementId)}`;
  document.head.appendChild(script);
  gaScriptLoaded = true;
}

// Expire a cookie across likely domain/path scopes so it is actually cleared
// regardless of which subdomain set it.
function expireCookie(name: string) {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }
  const past = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = new Set<string>(['', host, `.${host}`]);
  if (parts.length >= 2) {
    const root = parts.slice(-2).join('.');
    domains.add(`.${root}`);
  }
  domains.forEach((domain) => {
    const domainClause = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; ${past}; path=/${domainClause}`;
  });
}

function clearAnalyticsCookies() {
  if (typeof document === 'undefined') {
    return;
  }
  const patterns = [/^_ga/, /^_gid$/, /^_gat/, /^_clck$/, /^_clsk$/, /^CLID$/, /^MUID$/, /^ANONCHK$/, /^SM$/];
  document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]?.trim())
    .filter((name): name is string => Boolean(name))
    .forEach((name) => {
      if (patterns.some((pattern) => pattern.test(name))) {
        expireCookie(name);
      }
    });
}

// Apply a consent decision immediately. Idempotent and safe to call on every
// load and on every user change. Wired up via <CookieConsent onConsentChange />.
export function applyConsent(consent: ConsentCategories) {
  if (!getAnalyticsEnabled() || typeof window === 'undefined' || isSensitiveAnalyticsPath(window.location.pathname)) {
    return;
  }

  ensureGtagStub();
  setConsentDefaults();

  // --- Google Analytics (Consent Mode v2) ---
  window.gtag?.('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
  });

  if (consent.analytics) {
    loadGoogleAnalyticsOnce();

  } else {
    // Revoke storage and clear existing analytics cookies.
    clearAnalyticsCookies();
  }
}

export function initAnalytics() {
  if (!getAnalyticsEnabled() || typeof window === 'undefined' || typeof document === 'undefined' || isSensitiveAnalyticsPath(window.location.pathname)) {
    return;
  }

  ensureGtagStub();
  setConsentDefaults();
  // Honor the persisted choice (or GPC-aware default) on every load.
  applyConsent(getEffectiveConsent());
}

export function trackEvent(event: string, properties?: AnalyticsProperties) {
  if (!getAnalyticsEnabled() || typeof window === 'undefined' || isSensitiveAnalyticsPath(window.location.pathname)) {
    return;
  }

  if (!getEffectiveConsent().analytics) return;
  const payload = buildAnalyticsPayload(event, properties);
  const gaMeasurementId = getGaMeasurementId();

  if (gaMeasurementId && typeof window.gtag === 'function') {
    window.gtag('event', event, {
      ...payload.properties,
      page_location: `${window.location.origin}${window.location.pathname}`,
      page_referrer: payload.page.referrer,
      page_path: payload.page.path,
      page_title: payload.page.title,
    });
  }

  const analyticsEndpoint = getAnalyticsEndpoint();
  if (!analyticsEndpoint) {
    return;
  }

  const body = JSON.stringify(payload);

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const sent = navigator.sendBeacon(analyticsEndpoint, new Blob([body], { type: 'application/json' }));
    if (sent) {
      return;
    }
  }

  void fetch(analyticsEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // Analytics should never block customer-facing flows.
  });
}

export function trackPageView(path: string) {
  trackEvent('page_view', { path: safeAnalyticsUrl(path) });
}
